import React, { useCallback, useEffect, useState } from 'react';
import FlowStrip from '../../help/FlowStrip';
import StatusStagesChip from '../../help/StatusStagesChip';
import { salesFlow } from '../../help/flows';
import { QUOTATION_STAGES } from '../../help/statusStages';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Link, Paper, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { QuotationService } from '../../services/quotation.service';
import { SalesOrderService } from '../../services/salesOrder.service'; // W3-O
import {
  Quotation, QuotationStatus, QUOTATION_STATUS_COLORS,
} from '../../interfaces/sales/quotation.interface';
import { useToast } from '../../context/ToastContext';
import ScrollableTable from '../common/ScrollableTable';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import QuotationApprovalPanel from './QuotationApprovalPanel';   // S1
import ReassignDialog from './scope/ReassignDialog';
import { formatMoney, formatUnitPrice, humanizeEnum } from '../../utils/format';
import { formatFc, formatFcAtRate, isForeignCurrency, toastSaved } from '../../utils/fcDocuments';   // G5b
import { formatDate } from '../../utils/dateUtils';
import { hasDiscount } from '../../utils/salesLineDiscount';   // FW-2A
import { taxCodeLabel, useTaxCodeLookup } from '../tax/useTaxCodeLookup';

const STATUS_COLORS = QUOTATION_STATUS_COLORS;

const QuotationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const canManage = usePermission(PERMISSIONS.QUOTATION_MANAGE);
  const canConvert = usePermission(PERMISSIONS.QUOTATION_CONVERT);
  const canMakeOrder = usePermission(PERMISSIONS.SALES_ORDER_MANAGE); // W3-O
  const canReassign = usePermission(PERMISSIONS.SALES_REASSIGN);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [ordering, setOrdering] = useState(false); // W3-O

  const [quotation, setQuotation] = useState<Quotation | null>(null);
  const anyCodedLine = !!quotation?.lines?.some(l => l.taxCodeId);
  const anyDisc = hasDiscount(quotation?.lines ?? []);   // FW-2A
  const fc = isForeignCurrency(quotation?.currencyCode) ? quotation!.currencyCode! : null;   // G5b
  const taxLookup = useTaxCodeLookup(anyCodedLine ? quotation?.institutionId : null);
  const [loading, setLoading] = useState(true);

  // Send dialog
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sending, setSending] = useState(false);

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Confirm dialog
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);

  // Reject dialog state — collects required reason (mirrors SalesInvoiceDetail void dialog)
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  // Convert in-flight
  const [converting, setConverting] = useState(false);

  // Clone in-flight
  const [cloning, setCloning] = useState(false);

  // Email in-flight
  const [emailing, setEmailing] = useState(false);

  // PDF download in-flight
  const [downloading, setDownloading] = useState(false);

  const reload = useCallback(async () => {
    if (!id) return;
    try {
      const q = await QuotationService.getById(id);
      setQuotation(q);
    } catch (e) {
      toast.showApiError(e, 'Failed to load quotation');
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    reload().finally(() => setLoading(false));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Send — returns updated quotation; use setState not redundant reload
  const handleSend = async () => {
    if (!quotation) return;
    setSending(true);
    try {
      const updated = await QuotationService.send(quotation.id);
      setQuotation(updated);
      toast.showSuccess(updated.status === 'DRAFT' ? 'Sent for discount approval' : 'Quotation sent');   // S1: a draft stays a draft while it waits
      setSendDialogOpen(false);
    } catch (e) {
      toast.showApiError(e, 'Failed to send quotation');
    } finally {
      setSending(false);
    }
  };

  // ---- S1: take back the pending discount approval request ----
  const [withdrawing, setWithdrawing] = useState(false);
  const handleWithdraw = async () => {
    if (!quotation) return;
    setWithdrawing(true);
    try {
      setQuotation(await QuotationService.withdrawApproval(quotation.id));
      toast.showSuccess('Approval request withdrawn');
    } catch (e) {
      toast.showApiError(e, 'Failed to withdraw the request');
    } finally {
      setWithdrawing(false);
    }
  };
  // ---- end S1 ----

  // Delete — navigates away on success; surface 400 via toast
  const handleDelete = async () => {
    if (!quotation) return;
    setDeleting(true);
    try {
      await QuotationService.remove(quotation.id);
      toast.showSuccess('Quotation deleted');
      navigate('/sales/quotations');
    } catch (e) {
      toast.showApiError(e, 'Failed to delete quotation');
      setDeleteDialogOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  // Confirm — returns updated quotation; use setState
  const handleConfirm = async () => {
    if (!quotation) return;
    setConfirming(true);
    try {
      const updated = await QuotationService.confirm(quotation.id);
      setQuotation(updated);
      toast.showSuccess('Quotation confirmed');
      setConfirmDialogOpen(false);
    } catch (e) {
      toast.showApiError(e, 'Failed to confirm quotation');
    } finally {
      setConfirming(false);
    }
  };

  // Reject — collects required reason via dialog; returns updated quotation; use setState
  const handleReject = async () => {
    if (!quotation || !rejectReason.trim()) return;
    setRejecting(true);
    try {
      const updated = await QuotationService.reject(quotation.id, { reason: rejectReason.trim() });
      setQuotation(updated);
      toast.showSuccess('Quotation rejected');
      setRejectDialogOpen(false);
      setRejectReason('');
    } catch (e) {
      toast.showApiError(e, 'Failed to reject quotation');
    } finally {
      setRejecting(false);
    }
  };

  // Convert → SalesInvoice; navigate to the new invoice
  const handleConvert = async () => {
    if (!quotation) return;
    setConverting(true);
    try {
      const invoice = await QuotationService.convert(quotation.id);
      toastSaved(toast, 'Quotation converted to invoice', invoice.warnings);   // G5b: the invoice takes the rate of its own date
      navigate(`/sales/invoices/${invoice.id}`);
    } catch (e) {
      toast.showApiError(e, 'Failed to convert quotation');
    } finally {
      setConverting(false);
    }
  };

  // ---- W3-O: convert to a sales order instead of an invoice ----
  const handleConvertToOrder = async () => {
    if (!quotation) return;
    setOrdering(true);
    try {
      const order = await SalesOrderService.fromQuotation(quotation.id);
      toastSaved(toast, 'Sales order created from the quotation', order.warnings);   // G5b
      navigate(`/sales/orders/${order.id}`);
    } catch (e) {
      toast.showApiError(e, 'Failed to create the sales order');
    } finally {
      setOrdering(false);
    }
  };
  // ---- end W3-O ----

  // Clone → new draft Quotation; navigate to the new draft
  const handleClone = async () => {
    if (!quotation) return;
    setCloning(true);
    try {
      const cloned = await QuotationService.clone(quotation.id);
      toast.showSuccess('Quotation cloned');
      navigate(`/sales/quotations/${cloned.id}`);
    } catch (e) {
      toast.showApiError(e, 'Failed to clone quotation');
    } finally {
      setCloning(false);
    }
  };

  // Download PDF — Blob + in-flight guard (mirrors SalesInvoiceDetail)
  const handleDownloadPdf = async () => {
    if (!quotation) return;
    setDownloading(true);
    try {
      const blob = await QuotationService.downloadPdf(quotation.id);
      const url = URL.createObjectURL(blob);
      if (quotation.status === 'DRAFT') {
        window.open(url, '_blank', 'noopener');
        setTimeout(() => URL.revokeObjectURL(url), 60000);
        return;
      }
      const a = document.createElement('a');
      a.href = url;
      a.download = `quotation-${quotation.quotationNumber ?? quotation.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.showApiError(e, 'Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  };

  // Email — in-flight guard (mirrors SalesInvoiceDetail)
  const handleEmail = async () => {
    if (!quotation) return;
    setEmailing(true);
    try {
      await QuotationService.email(quotation.id);
      toast.showSuccess('Quotation emailed successfully');
    } catch (e) {
      toast.showApiError(e, 'Failed to email quotation');
    } finally {
      setEmailing(false);
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  if (!quotation) return <Box sx={{ p: 3 }}><Typography>Quotation not found.</Typography></Box>;

  const status: QuotationStatus = quotation.status;

  // Status-gated action map (mirrors SalesInvoiceDetail `can`)
  const can = {
    send: canManage && status === 'DRAFT' && quotation.approvalStatus !== 'PENDING' && quotation.approvalStatus !== 'REJECTED',   // S1
    withdraw: canManage && status === 'DRAFT' && quotation.approvalStatus === 'PENDING',
    edit: canManage && status === 'DRAFT' && quotation.approvalStatus !== 'PENDING',
    delete: canConvert && status === 'DRAFT',
    confirm: canConvert && status === 'SENT',
    reject: canManage && status === 'SENT',
    clone: canManage && (status === 'SENT' || status === 'CONFIRMED' || status === 'REJECTED' || status === 'EXPIRED'),
    convert: canConvert && status === 'CONFIRMED',
    // A draft can be previewed (opens in a new tab); email needs a sent quotation.
    pdf: true,
    email: canManage && status !== 'DRAFT',
  };

  return (
    <Box sx={{ p: 3 }} data-help-page="sales.quotation.page">
      <FlowStrip data-help="sales.quotation.flow" stages={salesFlow({ current: 'quotation', quotation: { no: quotation.quotationNumber ?? 'Draft' }, order: { id: quotation.convertedSalesOrderId }, invoices: [{ id: quotation.convertedInvoiceId }] })} />
      {/* Title + action buttons */}
      <Box data-help-section="sales.quotation.actions" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">
          {quotation.quotationNumber ?? 'Draft Quotation (unnumbered)'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>Salesperson: {quotation.salespersonName ?? 'Unassigned'}</Typography>
          {canReassign && <Button data-help="sales.quotation.reassign" onClick={() => setReassignOpen(true)}>Reassign</Button>}
          {reassignOpen && (
            <ReassignDialog open type="quotations" id={quotation.id} title={`Quotation ${quotation.quotationNumber ?? 'draft'}`} currentName={quotation.salespersonName}
              onClose={() => setReassignOpen(false)} onDone={reload} />
          )}
          {can.send && (
            <Button data-help="sales.quotation.send" variant="contained" color="primary" onClick={() => setSendDialogOpen(true)}>
              {quotation.approvalRequired && quotation.approvalStatus !== 'APPROVED' ? 'Send for approval' : 'Send'}
            </Button>
          )}
          {can.withdraw && (
            <Button data-help="sales.quotation.withdraw" variant="outlined" disabled={withdrawing} onClick={handleWithdraw}>Withdraw approval request</Button>
          )}
          {can.edit && (
            <Button data-help="sales.quotation.edit" variant="outlined" onClick={() => navigate(`/sales/quotations/${quotation.id}/edit`)}>
              Edit
            </Button>
          )}
          {can.delete && (
            <Button data-help="sales.quotation.delete" variant="outlined" color="error" onClick={() => setDeleteDialogOpen(true)}>
              Delete
            </Button>
          )}
          {can.confirm && (
            <Button data-help="sales.quotation.confirm" variant="contained" color="success" onClick={() => setConfirmDialogOpen(true)}>
              Confirm
            </Button>
          )}
          {can.reject && (
            <Button data-help="sales.quotation.reject" variant="outlined" color="error" onClick={() => setRejectDialogOpen(true)}>
              Reject
            </Button>
          )}
          {can.convert && (
            <Button data-help="sales.quotation.toInvoice" variant="contained" color="primary" onClick={handleConvert} disabled={converting}>
              {converting ? <CircularProgress size={20} /> : 'Convert to Invoice'}
            </Button>
          )}
          {can.convert && canMakeOrder && (
            <Button data-help="sales.quotation.toOrder" variant="outlined" onClick={handleConvertToOrder} disabled={ordering}>Convert to Sales Order</Button>
          )}
          {can.clone && (
            <Button data-help="sales.quotation.clone" variant="outlined" onClick={handleClone} disabled={cloning}>
              {cloning ? <CircularProgress size={20} /> : 'Clone'}
            </Button>
          )}
          {can.pdf && (
            <Button data-help="sales.quotation.pdf" variant="outlined" onClick={handleDownloadPdf} disabled={downloading}>
              {downloading ? <CircularProgress size={20} /> : (status === 'DRAFT' ? 'Preview PDF' : 'Download PDF')}
            </Button>
          )}
          {can.email && (
            <Button data-help="sales.quotation.email" variant="outlined" onClick={handleEmail} disabled={emailing}>
              {emailing ? <CircularProgress size={20} /> : 'Email'}
            </Button>
          )}
          {/* ---- FW-2C ---- */}
          {canManage && (
            <Button data-help="sales.quotation.recurring" variant="outlined" onClick={() => navigate(`/sales/recurring?prefillKind=QUOTATION&prefillDoc=${quotation.id}`)}>Make recurring</Button>
          )}
          {/* ---- end FW-2C ---- */}
          <Button data-help="sales.quotation.back" variant="outlined" onClick={() => navigate(-1)}>Back</Button>
        </Box>
      </Box>

      {status === 'DRAFT' && quotation.maxDiscountPct != null && Number(quotation.maxDiscountPct) > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Largest line discount: {quotation.maxDiscountPct}%</Typography>
      )}
      <QuotationApprovalPanel quotationId={quotation.id} approvalStatus={quotation.approvalStatus} approvalNote={quotation.approvalNote} onDecided={reload} />   {/* S1 */}

      {/* Header info */}
      <Paper data-help-section="sales.quotation.header" sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">Quotation Number</Typography>
            <Typography>{quotation.quotationNumber ?? '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Status</Typography>
            <Box sx={{ mt: 0.5 }}>
              <StatusStagesChip data-help="sales.quotation.statusStages" label={humanizeEnum(status)} status={status} stages={QUOTATION_STAGES} color={STATUS_COLORS[status]} />
            </Box>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Customer</Typography>
            <Typography>{quotation.customerName ?? quotation.customerId}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Quotation Date</Typography>
            <Typography>{formatDate(quotation.quotationDate)}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Valid Until</Typography>
            <Typography>{quotation.validUntil ? formatDate(quotation.validUntil) : '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Subtotal</Typography>
            <Typography>{formatMoney(quotation.subtotal)}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Tax Amount</Typography>
            <Typography>{formatMoney(quotation.taxAmount)}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Total Amount</Typography>
            <Typography fontWeight="bold">{formatMoney(quotation.totalAmount)}</Typography>
          </Box>
          {fc && (
            <Box sx={{ gridColumn: '1 / -1' }} data-testid="quotation-foreign-total">
              <Typography variant="body2" color="text.secondary">Currency</Typography>
              <Typography fontWeight="bold">
                {formatFcAtRate(fc, Number(quotation.foreignTotal ?? 0), Number(quotation.exchangeRate ?? 0), Number(quotation.totalAmount))}
              </Typography>
              <Typography variant="caption" color="text.secondary">Indicative rate: the invoice uses the rate of its own date.</Typography>
            </Box>
          )}
          {quotation.notes && (
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography variant="body2" color="text.secondary">Notes</Typography>
              <Typography>{quotation.notes}</Typography>
            </Box>
          )}
          {status === 'REJECTED' && quotation.rejectReason && (
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography variant="body2" color="text.secondary">Reject Reason</Typography>
              <Typography color="error.main">{quotation.rejectReason}</Typography>
            </Box>
          )}
          {status === 'CONVERTED' && quotation.convertedInvoiceId && (
            <Box>
              <Typography variant="body2" color="text.secondary">Converted Invoice</Typography>
              <Link
                component={RouterLink}
                to={`/sales/invoices/${quotation.convertedInvoiceId}`}
              >
                View Invoice
              </Link>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Line items table */}
      {quotation.lines.length > 0 && (
        <Paper data-help-section="sales.quotation.lines" sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Line Items</Typography>
          <ScrollableTable>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell data-help="sales.quotation.col.description">Description</TableCell>
                    <TableCell align="right" data-help="sales.quotation.col.quantity">Quantity</TableCell>
                    <TableCell align="right" data-help="sales.quotation.col.unitPrice">Unit Price</TableCell>
                    {fc && <TableCell align="right" data-help="sales.quotation.col.fcUnitPrice">{`Unit Price (${fc})`}</TableCell>}
                    {fc && <TableCell align="right" data-help="sales.quotation.col.fcNet">{`Net (${fc})`}</TableCell>}
                    {anyDisc && <TableCell align="right" data-help="sales.quotation.col.gross">Gross</TableCell>}   {/* FW-2A */}
                    {anyDisc && <TableCell align="right" data-help="sales.quotation.col.discount">Discount</TableCell>}
                    {anyCodedLine && <TableCell data-help="sales.quotation.col.taxCode">Tax code</TableCell>}
                    <TableCell align="right" data-help="sales.quotation.col.taxPct">Tax %</TableCell>
                    <TableCell align="right" data-help="sales.quotation.col.lineTax">Line Tax</TableCell>
                    <TableCell align="right" data-help="sales.quotation.col.lineTotal">Line Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {quotation.lines.map(line => (
                    <TableRow key={line.id}>
                      <TableCell sx={{ whiteSpace: 'normal', minWidth: 140 }}>{line.description}</TableCell>
                      <TableCell align="right">{line.quantity}</TableCell>
                      <TableCell align="right">{formatUnitPrice(line.unitPrice)}</TableCell>
                      {fc && <TableCell align="right">{formatFc(fc, line.foreignUnitPrice)}</TableCell>}
                      {fc && <TableCell align="right">{formatFc(fc, line.foreignAmount)}</TableCell>}
                      {anyDisc && <TableCell align="right">{formatMoney(line.grossAmount ?? line.lineTotal)}</TableCell>}   {/* FW-2A */}
                      {anyDisc && <TableCell align="right">{Number(line.discountAmount) > 0 ? `${formatMoney(line.discountAmount)}${line.discountPct != null ? ` (${line.discountPct}%)` : ''}` : '-'}</TableCell>}
                      {anyCodedLine && <TableCell>{taxCodeLabel(taxLookup, line.taxCodeId)}</TableCell>}
                      <TableCell align="right">{line.taxRatePct}%</TableCell>
                      <TableCell align="right">{formatMoney(line.lineTax)}</TableCell>
                      <TableCell align="right">{formatMoney(line.lineTotal)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
          </ScrollableTable>
        </Paper>
      )}

      {/* Send confirm dialog */}
      <Dialog open={sendDialogOpen} onClose={() => setSendDialogOpen(false)}>
        <DialogTitle>Send this quotation?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {quotation.approvalRequired && quotation.approvalStatus !== 'APPROVED'
              ? 'This quotation has a discount above your limit. It will go to the approver first and stays a draft until they approve it.'
              : 'Sending the quotation will assign a quotation number and mark it as sent. You can still confirm, reject, or clone it after sending.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button data-help="sales.quotation.dialogCancel" onClick={() => setSendDialogOpen(false)} disabled={sending}>Cancel</Button>
          <Button data-help="sales.quotation.send.confirm" variant="contained" color="primary" onClick={handleSend} disabled={sending}>
            {sending ? <CircularProgress size={20} /> : 'Confirm Send'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete this draft quotation?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete the draft quotation. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button data-help="sales.quotation.dialogCancel" onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>Cancel</Button>
          <Button data-help="sales.quotation.delete.confirm" variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm quotation dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>Confirm this quotation?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Confirming the quotation marks it as accepted. You can then convert it to an invoice.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button data-help="sales.quotation.dialogCancel" onClick={() => setConfirmDialogOpen(false)} disabled={confirming}>Cancel</Button>
          <Button data-help="sales.quotation.confirm.confirm" variant="contained" color="success" onClick={handleConfirm} disabled={confirming}>
            {confirming ? <CircularProgress size={20} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject dialog — collects required reason (mirrors SalesInvoiceDetail void dialog) */}
      <Dialog open={rejectDialogOpen} onClose={() => { setRejectDialogOpen(false); setRejectReason(''); }} fullWidth maxWidth="sm">
        <DialogTitle>Reject this quotation?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Rejecting the quotation marks it as declined. A reject reason is required.
          </DialogContentText>
          <TextField
            data-help="sales.quotation.reject.reason"
            label="Reject Reason"
            value={rejectReason}
            onChange={e => setRejectReason(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button
            data-help="sales.quotation.dialogCancel"
            onClick={() => { setRejectDialogOpen(false); setRejectReason(''); }}
            disabled={rejecting}
          >
            Cancel
          </Button>
          <Button
            data-help="sales.quotation.reject.confirm"
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={rejecting || rejectReason.trim().length === 0}
          >
            {rejecting ? <CircularProgress size={20} /> : 'Confirm Reject'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default QuotationDetail;
